import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { api, API as API_BASE } from "../lib/api";
import { getToken } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Users, Stethoscope, CheckCircle2, IndianRupee, Clock, TrendingUp, ShieldAlert, Sparkles, RefreshCw, Phone, MessageCircle, FileText, Search, UserCheck, Download, BadgeCheck, RotateCcw, X, BarChart3 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import CaseOutcomesAdmin from "../components/CaseOutcomesAdmin";
import MetaLeadsSection from "../components/MetaLeadsSection";
import ReferralLeaderboard from "../components/ReferralLeaderboard";
import AdminEarnings from "../components/AdminEarnings";
import WhatsAppInboxAdmin from "../components/WhatsAppInboxAdmin";
import AdminCoupons from "../components/AdminCoupons";

export default function AdminDashboard() {
  const { t } = useI18n();
  const [pending, setPending] = useState([]);
  const [all, setAll] = useState([]);
  const [cases, setCases] = useState([]);
  // Force a re-render every 60s so the SLA countdown ticks down live.
  const [, setSlaTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSlaTick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  // 🔔 Emergency ringtone — plays a chime + browser push the moment a new
  // Emergency case flips to `paid` in the polled cases feed. Requires user
  // gesture on some browsers, so we also request Notification permission
  // once on mount.
  const seenEmergencyIds = useRef(new Set());
  useEffect(() => {
    if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "default") {
      try { Notification.requestPermission(); } catch (e) { /* ignore */ }
    }
  }, []);
  useEffect(() => {
    if (!cases || cases.length === 0) return;
    const activeUrgent = cases.filter(
      (c) => (c.service_type === "EMERGENCY" || c.priority === "urgent") &&
             c.payment_status === "paid" &&
             c.status !== "completed"
    );
    // Skip the initial paint so we don't chime for pre-existing cases.
    if (seenEmergencyIds.current.size === 0) {
      activeUrgent.forEach((c) => seenEmergencyIds.current.add(c.id));
      return;
    }
    const newlyPaid = activeUrgent.filter((c) => !seenEmergencyIds.current.has(c.id));
    if (newlyPaid.length > 0) {
      newlyPaid.forEach((c) => seenEmergencyIds.current.add(c.id));
      // Chime via Web Audio (no MP3 asset dependency)
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        [880, 1320].forEach((f, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "sine";
          osc.frequency.value = f;
          gain.gain.setValueAtTime(0.001, ctx.currentTime + i * 0.35);
          gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + i * 0.35 + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.35 + 0.32);
          osc.connect(gain).connect(ctx.destination);
          osc.start(ctx.currentTime + i * 0.35);
          osc.stop(ctx.currentTime + i * 0.35 + 0.35);
        });
      } catch (e) { /* audio autoplay may be blocked */ }
      // Browser push
      const c = newlyPaid[0];
      try {
        if ("Notification" in window && Notification.permission === "granted") {
          new Notification("🚨 URGENT: Emergency case paid", {
            body: `${c.patient_name || "A patient"} · ${c.currency === "USD" ? `$${c.fee}` : `₹${c.fee}`} · <24h SLA`,
            tag: `emg-${c.id}`,
            requireInteraction: true,
          });
        }
      } catch (e) { /* ignore */ }
      toast.error(`🚨 New Emergency case: ${c.patient_name || "Patient"}`, { duration: 8000 });
    }
  }, [cases]);
  const [stats, setStats] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [audit, setAudit] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadsFilter, setLeadsFilter] = useState("new");
  const [aiStatus, setAiStatus] = useState(null);
  const [aiChecking, setAiChecking] = useState(false);

  const [payouts, setPayouts] = useState({ months: [], items: [] });
  const [payoutMonth, setPayoutMonth] = useState("");
  const [hospitals, setHospitals] = useState([]);
  const [hospFilter, setHospFilter] = useState("pending");
  const [reviews, setReviews] = useState([]);
  const [acqFunnel, setAcqFunnel] = useState(null);
  const [pendingQueue, setPendingQueue] = useState([]);
  const [funnelDays, setFunnelDays] = useState(30);
  // Interakt / WhatsApp template health — polled once on mount for the top banner.
  const [interaktHealth, setInteraktHealth] = useState(null);
  const [firingTestLead, setFiringTestLead] = useState(false);
  // Free-consult attribution funnel + admin grant tool state
  const [attribution, setAttribution] = useState(null);
  const [grantQuery, setGrantQuery] = useState("");
  const [grantFound, setGrantFound] = useState(null);
  const [grantReason, setGrantReason] = useState("");
  const [grantBusy, setGrantBusy] = useState(false);

  const load = async () => {
    const [p, d, c, s, f, a, ai, l, po, h, rv, af, pq] = await Promise.all([
      api.get("/admin/doctors?status=pending"),
      api.get("/admin/doctors"),
      api.get("/admin/consultations"),
      api.get("/admin/analytics"),
      api.get("/admin/funnel"),
      api.get("/admin/audit?limit=50"),
      api.get("/admin/ai/status"),
      api.get(`/admin/leads?limit=100${leadsFilter === "all" ? "" : `&status=${leadsFilter}`}`),
      api.get(`/admin/payouts${payoutMonth ? `?month=${payoutMonth}` : ""}`),
      api.get(`/admin/hospitals${hospFilter === "all" ? "" : `?status=${hospFilter}`}`),
      api.get("/admin/reviews?limit=100"),
      api.get(`/admin/funnel/acquisition?days=${funnelDays}`),
      api.get("/admin/doctors/pending-queue?limit=8"),
    ]);
    setPending(p.data); setAll(d.data); setCases(c.data); setStats(s.data);
    setFunnel(f.data); setAudit(a.data); setAiStatus(ai.data);
    setLeads(l.data.items || []);
    setPayouts(po.data);
    setHospitals(h.data || []);
    setReviews(rv.data?.items || []);
    setAcqFunnel(af.data);
    setPendingQueue(pq.data?.items || []);
  };

  const toggleReviewVisibility = async (rid, next, name) => {
    try {
      await api.patch(`/admin/reviews/${rid}/visibility`, { publish_publicly: next });
      toast.success(next ? "Review now visible on the public site" : "Review hidden from the public site");
      setReviews((rs) => rs.map((r) => r.id === rid ? { ...r, publish_publicly: next } : r));
    } catch (e) { toast.error("Could not change visibility"); }
  };
  const deleteReview = async (rid) => {
    if (!window.confirm("Permanently delete this review? This recomputes the doctor's rating.")) return;
    try {
      await api.delete(`/admin/reviews/${rid}`);
      toast.success("Review deleted");
      setReviews((rs) => rs.filter((r) => r.id !== rid));
    } catch (e) { toast.error("Delete failed"); }
  };

  const decideHospital = async (id, approved, name) => {
    try {
      await api.post(`/admin/hospitals/${id}/approval`, { approved, note: approved ? "Approved" : "Rejected" });
      toast.success(`${name || "Hospital"} ${approved ? "approved" : "rejected"}`);
      load();
    } catch (e) { toast.error("Action failed"); }
  };

  const updateLeadStatus = async (id, status) => {
    try {
      await api.put(`/admin/leads/${id}/status`, { status });
      toast.success(`Lead marked as ${status}`);
      load();
    } catch (e) {
      toast.error("Could not update lead");
    }
  };

  const checkAiUpgrade = async () => {
    setAiChecking(true);
    try {
      const r = await api.post("/admin/ai/check-upgrade");
      const upgraded = (r.data.results || []).filter(x => x.upgraded);
      if (upgraded.length > 0) {
        toast.success(`Upgraded: ${upgraded.map(x => `${x.provider} → ${x.active}`).join(", ")}`);
      } else {
        toast("Already on the newest accepted model.");
      }
      const ai = await api.get("/admin/ai/status");
      setAiStatus(ai.data);
    } catch { toast.error("Upgrade check failed"); }
    finally { setAiChecking(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [leadsFilter, payoutMonth, hospFilter, funnelDays]);

  // Interakt template health — fetch once on mount so the top-of-dashboard
  // banner can warn the founder immediately if the WhatsApp key is dead
  // (else all outbound WA templates silently fail).
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/whatsapp/interakt-health");
        setInteraktHealth(r.data);
      } catch (e) { /* non-fatal — banner just won't show */ }
    })();
    (async () => {
      try {
        const r = await api.get("/admin/free-consult-attribution");
        setAttribution(r.data);
      } catch (e) { /* non-fatal */ }
    })();
  }, []);

  const lookupPatient = async () => {
    if (!grantQuery.trim()) return;
    try {
      const r = await api.get("/admin/patients/lookup", { params: { q: grantQuery.trim() } });
      setGrantFound(r.data);
    } catch (e) {
      setGrantFound(null);
      toast.error(e.response?.data?.detail || "Patient not found");
    }
  };

  const grantFreeConsult = async () => {
    if (!grantFound?.id || grantBusy) return;
    setGrantBusy(true);
    try {
      await api.post(`/admin/patients/${grantFound.id}/grant-free-consult`, {
        reason: grantReason.trim() || "manual admin comp",
        expiry_days: 30,
      });
      toast.success(`🎁 Free consult granted to ${grantFound.name || grantFound.email}`);
      // Refresh the card + attribution widget
      await lookupPatient();
      try {
        const r = await api.get("/admin/free-consult-attribution");
        setAttribution(r.data);
      } catch (e) { /* ignore */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Grant failed");
    } finally { setGrantBusy(false); }
  };

  const revokeFreeConsult = async () => {
    if (!grantFound?.id || grantBusy) return;
    if (!window.confirm("Revoke this patient's free consult?")) return;
    setGrantBusy(true);
    try {
      await api.post(`/admin/patients/${grantFound.id}/revoke-free-consult`, {
        reason: grantReason.trim() || "admin revoke",
      });
      toast.success("Free consult revoked");
      await lookupPatient();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revoke failed");
    } finally { setGrantBusy(false); }
  };

  const fireTestLead = async () => {
    if (firingTestLead) return;
    setFiringTestLead(true);
    try {
      const r = await api.post("/admin/leads/test-fire", {
        name: "QA Test Lead",
        phone: "+919999900001",
        query: "Brain MRI second opinion — smoke test",
      });
      if (r.data?.whatsapp_fired) {
        toast.success("✅ Test lead inserted + WhatsApp welcome sent");
      } else {
        toast.success(`✅ Test lead inserted (WA: ${r.data?.whatsapp_error || "skipped"})`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fire test lead failed");
    } finally { setFiringTestLead(false); }
  };

  const decide = async (id, approved) => {
    try {
      await api.post(`/admin/doctors/${id}/approval`, { approved });
      toast.success(approved ? "Doctor approved" : "Doctor rejected");
      load();
    } catch { toast.error("Action failed"); }
  };

  // ─── Manual Reg-No edit (admin types it from the uploaded certificate) ───
  const [editingRegId, setEditingRegId] = useState(null);
  const [regDraft, setRegDraft] = useState("");
  const [regSaving, setRegSaving] = useState(false);
  const startEditReg = (d) => { setEditingRegId(d.id); setRegDraft(d.registration_number || ""); };
  const cancelEditReg = () => { setEditingRegId(null); setRegDraft(""); };
  const saveDoctorReg = async (id) => {
    setRegSaving(true);
    try {
      const r = await api.put(`/admin/doctors/${id}/registration-number`, {
        registration_number: regDraft.trim(),
      });
      setAll((rows) => rows.map((d) => d.id === id ? { ...d, registration_number: r.data.registration_number } : d));
      toast.success(r.data.registration_number ? "Registration number saved" : "Registration number cleared");
      cancelEditReg();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setRegSaving(false); }
  };

  // ─── Lead manual-edit + IP→city resolver ────────────────────────────────
  const [editLeadId, setEditLeadId] = useState(null);
  const [leadDraft, setLeadDraft] = useState({ phone: "", email: "", whatsapp: "", admin_notes: "" });
  const [leadSaving, setLeadSaving] = useState(false);
  const [cityResolving, setCityResolving] = useState(null);
  const startEditLead = (l) => {
    setEditLeadId(l.id);
    setLeadDraft({
      phone: l.phone || "",
      email: l.email || "",
      whatsapp: l.whatsapp || "",
      admin_notes: l.admin_notes || "",
    });
  };
  const cancelEditLead = () => { setEditLeadId(null); setLeadDraft({ phone: "", email: "", whatsapp: "", admin_notes: "" }); };
  const saveLead = async () => {
    setLeadSaving(true);
    try {
      const payload = {};
      ["phone", "email", "whatsapp", "admin_notes"].forEach((k) => {
        payload[k] = leadDraft[k].trim();
      });
      const r = await api.patch(`/admin/leads/${editLeadId}`, payload);
      setLeads((rows) => rows.map((l) => l.id === editLeadId ? { ...l, ...(r.data.lead || {}) } : l));
      toast.success("Lead updated");
      cancelEditLead();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setLeadSaving(false); }
  };
  const resolveCity = async (lid) => {
    setCityResolving(lid);
    try {
      const r = await api.post(`/admin/leads/${lid}/resolve-city`);
      setLeads((rows) => rows.map((l) => l.id === lid ? { ...l, city_from_ip: r.data.city_from_ip || r.data.city, region_from_ip: r.data.region_from_ip || r.data.region } : l));
      toast.success(r.data.cached ? "City already resolved" : `Resolved: ${r.data.city_from_ip || r.data.city}, ${r.data.region_from_ip || r.data.region || ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resolve");
    } finally { setCityResolving(null); }
  };

  // ─── WhatsApp helpers for leads ─────────────────────────────────────────
  const waNumber = (l) => String(l.whatsapp || l.phone || "").replace(/[^\d]/g, "");
  const isWaLead = (l) => {
    const src = (l.source || "").toLowerCase();
    return l.channel === "whatsapp" || src.includes("wa") || src === "fab" || (!!l.whatsapp);
  };
  const waChatUrl = (l) => {
    const num = waNumber(l);
    if (!num) return null;
    const firstName = (l.name || "there").split(" ")[0].slice(0, 30);
    const concern = (l.query || l.chief_complaint || "").trim();
    const concernSnippet = concern ? ` about "${concern.slice(0, 90)}"` : "";
    const greet =
      `Hi ${firstName}, this is the NeuroOpinion India patient care team. ` +
      `Thank you for reaching out${concernSnippet}. ` +
      `Could we schedule a quick call with one of our specialists, or share any reports/scans you have?`;
    return `https://wa.me/${num}?text=${encodeURIComponent(greet)}`;
  };
  const openWaChat = async (l) => {
    // Fully server-side: Interakt fires `callback_outreach` template from the
    // WABA (+91 83176 74160) to the lead. Admin's personal WhatsApp is never
    // used. Falls back to opening wa.me if the template isn't approved yet.
    let sent = false;
    let detail = null;
    try {
      const r = await api.post(`/admin/leads/${l.id}/send-outreach`);
      if (r.data?.ok === false) {
        detail = r.data?.detail;
      } else {
        sent = true;
        toast.success(`WhatsApp sent from business (+91 83176 74160) · ID ${(r.data?.interakt_id || "").slice(0, 8)}`);
        setLeads((rows) => rows.map((x) => x.id === l.id ? { ...x, status: "contacted" } : x));
      }
    } catch (e) {
      detail = e.response?.data?.detail;
    }
    if (!sent) {
      // Fallback: if template not approved yet, open wa.me on admin's device so
      // ops can at least type manually. This keeps the button useful until Meta
      // approves the new `callback_outreach` template.
      const url = waChatUrl(l);
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
        toast.warning("WABA template not approved yet — opened your WhatsApp instead. Submit the `callback_outreach` template in Interakt to enable auto-send.");
        if (l.status === "new") updateLeadStatus(l.id, "contacted");
      } else {
        toast.error(typeof detail === "string" ? detail : "No WhatsApp number on this lead");
      }
    }
  };

  const cards = [
    { icon: Users, label: "Total Patients", value: stats?.total_patients ?? "—", tone: "blue" },
    { icon: FileText, label: "Total Cases", value: stats?.total_cases ?? "—", tone: "slate" },
    { icon: Clock, label: "Pending Cases", value: stats?.pending_cases ?? "—", tone: "amber" },
    { icon: CheckCircle2, label: "Completed Cases", value: stats?.completed_cases ?? "—", tone: "emerald" },
    { icon: IndianRupee, label: "Total Revenue", value: `₹${(stats?.gross_revenue ?? 0).toLocaleString()}`, tone: "teal" },
    { icon: IndianRupee, label: "Doctor Payout (70%)", value: `₹${(stats?.doctor_payout ?? 0).toLocaleString()}`, tone: "blue" },
  ];

  // Cases tab state
  const [caseSearch, setCaseSearch] = useState("");
  const [caseStatus, setCaseStatus] = useState("all");
  const [caseSource, setCaseSource] = useState("all"); // "all" | "whatsapp_bot_v3" | "web"
  const [hideRefunded, setHideRefunded] = useState(true); // by default hide old refunded rows
  const [assigningId, setAssigningId] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  const submitAdminReject = async () => {
    if (!rejectFor || rejectReason.trim().length < 10) {
      toast.error("Please provide a reason (min 10 chars).");
      return;
    }
    setRejecting(true);
    try {
      const r = await api.post(`/admin/consultations/${rejectFor.id}/reject`, { reason: rejectReason.trim() });
      if (r.data.refund_status === "refunded") {
        toast.success(`Case rejected · ₹${r.data.amount} refunded via ${r.data.gateway}`);
      } else if (r.data.refund_status === "not_required") {
        toast.success("Case rejected (no payment to refund)");
      } else {
        toast.message(`Case rejected — refund status: ${r.data.refund_status}`);
      }
      setRejectFor(null);
      setRejectReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reject case");
    } finally { setRejecting(false); }
  };

  // Retry a rejected international case using PayPal USD. Aman-rescue button.
  const retryAsUSD = async (c) => {
    if (!window.confirm(`Generate a new PayPal USD payment link for ${c.patient_name}?\n\nThis will:\n• Detect the country from their phone (${c.patient_phone || "unknown"})\n• Set the correct USD tier ($50 Africa / $100 International)\n• Create a fresh PayPal order\n\nProceed?`)) return;
    try {
      const r = await api.post(`/admin/consultations/${c.id}/retry-usd`);
      if (r.data?.payment_link) {
        try { await navigator.clipboard.writeText(r.data.payment_link); } catch (e) { /* ignore */ }
        toast.success(`Retry created · $${r.data.amount_usd} ${r.data.pricing_tier} · link copied`);
        window.open(r.data.payment_link, "_blank");
        try { const fresh = await api.get("/admin/consultations"); setCases(fresh.data || []); } catch (e) { /* ignore refresh error */ }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    }
  };


  const approvedDoctors = (all || []).filter(d => d.status === "approved");

  const filteredCases = (cases || []).filter(c => {
    if (caseStatus !== "all" && c.status !== caseStatus) return false;
    if (caseSource === "whatsapp_bot_v3" && c.source !== "whatsapp_bot_v3") return false;
    if (caseSource === "web" && c.source === "whatsapp_bot_v3") return false;
    if (hideRefunded && (c.status === "refunded" || c.status === "rejected")) return false;
    if (caseSearch) {
      const q = caseSearch.toLowerCase();
      if (!(c.patient_name?.toLowerCase().includes(q)
            || c.doctor_name?.toLowerCase().includes(q)
            || (c.wa_bot_case_id || "").toLowerCase().includes(q)
            || (c.id || "").toLowerCase().includes(q))) {
        return false;
      }
    }
    return true;
  });

  const botCaseCount = (cases || []).filter(c => c.source === "whatsapp_bot_v3").length;

  const assignDoctor = async (cid, doctor_id) => {
    if (!doctor_id) return;
    try {
      await api.put(`/admin/consultations/${cid}/assign?doctor_id=${doctor_id}`);
      toast.success("Doctor assigned");
      setAssigningId(null);
      load();
    } catch {
      toast.error("Could not assign doctor");
    }
  };

  const settlePayout = async (doctor_id, month, settled) => {
    if (!doctor_id) {
      toast.error("Cannot settle 'Unassigned' rows");
      return;
    }
    try {
      await api.put(`/admin/payouts/settle?doctor_id=${doctor_id}&month=${month}&settled=${settled}`);
      toast.success(settled ? "Marked as paid" : "Reverted");
      load();
    } catch {
      toast.error("Action failed");
    }
  };

  const exportPayoutsCSV = () => {
    const rows = payouts.items || [];
    if (!rows.length) { toast.error("No data to export"); return; }
    const headers = ["Month", "Doctor", "Specialization", "Email", "Cases", "Completed", "Gross Revenue (INR)", "Doctor Share 70% (INR)", "Platform Share 30% (INR)", "Settled", "Settled At"];
    const escape = (v) => {
      if (v == null) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [
      headers.join(","),
      ...rows.map(r => [
        r.month, r.doctor_name, r.specialization || "", r.doctor_email || "",
        r.cases, r.completed, r.gross, r.doctor_share, r.platform_share,
        r.settled ? "Yes" : "No",
        r.settled_at || "",
      ].map(escape).join(",")),
    ].join("\n");
    const stamp = new Date().toISOString().slice(0, 10);
    const fname = `neuroopinion-payouts${payoutMonth ? `-${payoutMonth}` : ""}-${stamp}.csv`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = fname;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("CSV downloaded");
  };

  const STATUS_LABELS = {
    pending_payment: { text: "Pending payment", cls: "bg-slate-100 text-slate-700 border-slate-200" },
    awaiting_review: { text: "Awaiting review", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    under_review:    { text: "Under review",    cls: "bg-blue-50 text-blue-700 border-blue-200" },
    completed:       { text: "Completed",       cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    refunded:        { text: "Refunded",        cls: "bg-rose-50 text-rose-700 border-rose-200" },
  };
  const PAY_LABELS = {
    paid:   { text: "Paid",   cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    unpaid: { text: "Unpaid", cls: "bg-slate-100 text-slate-600 border-slate-200" },
    failed: { text: "Failed", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-700">Admin</div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="font-heading text-3xl font-semibold text-slate-900 mt-1">{t("analytics")}</h1>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={fireTestLead}
              disabled={firingTestLead}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 transition disabled:opacity-50"
              data-testid="admin-fire-test-lead-btn"
              title="Insert a synthetic Meta Ad lead + fire the outreach WhatsApp template (safe smoke test)"
            >
              📤 {firingTestLead ? "Firing…" : "Fire test lead"}
            </button>
            <Link
              to="/admin/leads-kanban"
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition"
              data-testid="admin-kanban-link"
            >
              📋 Lead Kanban
            </Link>
            <Link
              to="/admin/whatsapp-diag"
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-green-200 bg-green-50 text-green-700 hover:bg-green-100 transition"
              data-testid="admin-wa-diag-link"
            >
              <MessageCircle className="w-3.5 h-3.5"/> WhatsApp diagnostics
            </Link>
            {/* One-click helpers for refreshing social-preview caches after
                a homepage / OG-tag change. Both open in a new tab and
                pre-fill the homepage URL so the founder can re-scrape in
                ~2 seconds without leaving the console. */}
            <a
              href="https://developers.facebook.com/tools/debug/?q=https%3A%2F%2Fneuroopinion.in%2F"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 transition"
              data-testid="admin-fb-og-refresh-link"
              title="Force Facebook / WhatsApp to re-scrape the homepage OG tags"
            >
              🔵 Refresh FB / WhatsApp OG
            </a>
            <a
              href="https://cards-dev.twitter.com/validator"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 transition"
              data-testid="admin-tw-card-refresh-link"
              title="Validate & refresh Twitter/X card preview"
            >
              🐦 Refresh X card
            </a>
          </div>
        </div>

        {/* Interakt template health banner — surfaces WA API key status so
            outbound template failures don't silently rot in the background. */}
        {interaktHealth && (() => {
          const ok = interaktHealth.interakt_enabled && interaktHealth.api_key_valid;
          const tone = ok
            ? "border-emerald-200 bg-emerald-50 text-emerald-800"
            : "border-rose-300 bg-rose-50 text-rose-800";
          const tplCount = Object.keys(interaktHealth.templates || {}).length;
          return (
            <div
              className={`mt-4 rounded-xl border p-3 flex items-start gap-3 text-sm ${tone}`}
              data-testid="admin-interakt-health-banner"
            >
              <span className="text-xl leading-none">{ok ? "🟢" : "🔴"}</span>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold">
                  Interakt WhatsApp: {ok ? "healthy" : "⚠ key invalid or disabled"}
                </div>
                <div className="text-xs mt-0.5 opacity-90">
                  Key <span className="font-mono">{interaktHealth.api_key_masked || "—"}</span>
                  {" · "}{tplCount} templates registered
                  {" · "}sender <span className="font-mono">{interaktHealth.configured_public_whatsapp || "—"}</span>
                  {interaktHealth.reason && <span className="ml-2 text-rose-700">— {interaktHealth.reason}</span>}
                </div>
              </div>
              <Link
                to="/admin/whatsapp-diag"
                className="text-xs font-semibold underline underline-offset-2 whitespace-nowrap"
                data-testid="admin-interakt-diag-link"
              >
                Open diagnostics →
              </Link>
            </div>
          );
        })()}

        {/* Free-Consult funnel + admin grant tool. Side-by-side on desktop. */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Attribution / funnel widget */}
          <Card className="p-5 border-slate-200" data-testid="admin-free-consult-funnel">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="font-heading font-semibold text-slate-900 text-base">
                🎁 Free-consult grants (historical + admin manual)
              </div>
              {attribution?.leads?.conversion_pct !== undefined && (
                <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid="funnel-conversion-badge">
                  {attribution.leads.conversion_pct}% consumed
                </span>
              )}
            </div>
            {!attribution && <p className="text-xs text-slate-500 mt-2">Loading funnel…</p>}
            {attribution && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-center">
                  <div className="rounded-lg border border-slate-100 bg-slate-50 p-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Leads</div>
                    <div className="text-lg font-bold text-slate-900" data-testid="funnel-leads-total">{attribution.leads.total}</div>
                  </div>
                  <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Consumed</div>
                    <div className="text-lg font-bold text-emerald-900" data-testid="funnel-leads-consumed">{attribution.leads.consumed}</div>
                  </div>
                  <div className="rounded-lg border border-blue-100 bg-blue-50 p-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-blue-700">Free cases</div>
                    <div className="text-lg font-bold text-blue-900" data-testid="funnel-cases-total">{attribution.cases.total}</div>
                  </div>
                  <div className="rounded-lg border border-violet-100 bg-violet-50 p-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-violet-700">Completed</div>
                    <div className="text-lg font-bold text-violet-900" data-testid="funnel-cases-completed">{attribution.cases.completed}</div>
                  </div>
                </div>
                <div className="mt-3 text-xs text-slate-500 flex items-center gap-3 flex-wrap">
                  <span>Eligible users: <b className="text-slate-800">{attribution.users.eligible}</b></span>
                  <span>· Used: <b className="text-slate-800">{attribution.users.used}</b></span>
                  <span>· Admin-manual grants: <b className="text-slate-800">{attribution.users.admin_manual_grants}</b></span>
                  <span>· Rejected/refunded: <b className="text-slate-800">{attribution.cases.rejected}</b></span>
                </div>
                {attribution.campaigns?.length > 0 && (
                  <div className="mt-3 border-t border-slate-100 pt-3">
                    <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Top campaigns</div>
                    <div className="space-y-1">
                      {attribution.campaigns.slice(0, 5).map((c) => (
                        <div key={c.campaign_name} className="flex items-center justify-between text-xs text-slate-700">
                          <span className="truncate">{c.campaign_name}</span>
                          <span className="text-slate-500">
                            <b className="text-slate-800">{c.consumed}</b>/{c.leads}
                            <span className="ml-2 text-emerald-700 font-semibold">{c.conversion_pct}%</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </Card>

          {/* Admin grant / revoke tool */}
          <Card className="p-5 border-slate-200" data-testid="admin-grant-free-consult-tool">
            <div className="font-heading font-semibold text-slate-900 text-base">🎁 Grant free consult</div>
            <p className="text-xs text-slate-500 mt-1">
              Comp any patient (hardship, VIP, referral) — 30-day expiry, one Standard tier case. Search by email or phone.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <input
                type="text"
                value={grantQuery}
                onChange={(e) => setGrantQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") lookupPatient(); }}
                placeholder="patient@example.com or +91 98765 43210"
                data-testid="grant-lookup-input"
                className="flex-1 h-9 rounded-md border border-slate-300 px-3 text-sm"
              />
              <button
                type="button"
                onClick={lookupPatient}
                data-testid="grant-lookup-btn"
                className="h-9 px-4 text-xs font-semibold rounded-md bg-slate-900 text-white hover:bg-slate-800"
              >
                Find
              </button>
            </div>
            {grantFound && (
              <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="grant-patient-card">
                <div className="text-sm font-semibold text-slate-900">{grantFound.name || "(unnamed)"}</div>
                <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-2">
                  <span>{grantFound.email || "—"}</span>
                  <span>· {grantFound.phone || "—"}</span>
                  {grantFound.country && <span>· {grantFound.country}</span>}
                </div>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {grantFound.free_consultation_eligible && !grantFound.free_consultation_used && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid="grant-status-eligible">✅ Eligible</span>
                  )}
                  {grantFound.free_consultation_used && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-200 text-slate-700" data-testid="grant-status-used">Already used</span>
                  )}
                  {!grantFound.free_consultation_eligible && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600" data-testid="grant-status-none">Not eligible</span>
                  )}
                  {grantFound.free_consultation_source && (
                    <span className="text-[10px] font-mono text-slate-500">src: {grantFound.free_consultation_source}</span>
                  )}
                </div>
                <input
                  type="text"
                  value={grantReason}
                  onChange={(e) => setGrantReason(e.target.value)}
                  placeholder="Reason (optional, for audit trail)"
                  data-testid="grant-reason-input"
                  className="mt-3 w-full h-9 rounded-md border border-slate-300 px-3 text-xs"
                />
                <div className="mt-3 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={grantFreeConsult}
                    disabled={grantBusy}
                    data-testid="grant-apply-btn"
                    className="h-9 px-4 text-xs font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {grantBusy ? "Working…" : "🎁 Grant / Refresh (30 days)"}
                  </button>
                  {grantFound.free_consultation_eligible && (
                    <button
                      type="button"
                      onClick={revokeFreeConsult}
                      disabled={grantBusy}
                      data-testid="grant-revoke-btn"
                      className="h-9 px-4 text-xs font-semibold rounded-md border border-rose-300 bg-white text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                    >
                      Revoke
                    </button>
                  )}
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Acquisition funnel — founder-first dashboard */}
        {acqFunnel && (
          <Card className="mt-6 p-6 border-slate-200" data-testid="admin-acq-funnel">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-700"/> Acquisition funnel
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  WhatsApp leads → Bookings → Paid → Opinion → Reviewed → 14d-Upsell. The single dashboard to check every morning.
                </p>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-slate-500">Window:</span>
                {[7, 30, 90].map((d) => (
                  <button
                    key={d}
                    onClick={() => setFunnelDays(d)}
                    data-testid={`funnel-window-${d}`}
                    className={`h-7 px-2.5 rounded-full font-semibold border transition ${
                      funnelDays === d
                        ? "bg-blue-700 text-white border-blue-700"
                        : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                    }`}
                  >{d}d</button>
                ))}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-3">
              {acqFunnel.steps.map((s, i) => {
                const max = Math.max(1, acqFunnel.steps[0].count);
                const widthPct = Math.max(15, Math.round(100 * s.count / max));
                const colour =
                  i === 0 ? "from-green-500 to-emerald-600" :
                  i === 1 ? "from-blue-500 to-blue-700"      :
                  i === 2 ? "from-violet-500 to-violet-700"  :
                  i === 3 ? "from-amber-500 to-amber-700"    :
                  i === 4 ? "from-rose-500 to-rose-700"      :
                            "from-purple-500 to-fuchsia-700";
                return (
                  <div key={s.key} className="rounded-xl border border-slate-200 p-3" data-testid={`funnel-step-${s.key}`}>
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{s.label}</div>
                    <div className="mt-1 font-heading text-2xl font-bold text-slate-900 tabular-nums">{s.count.toLocaleString()}</div>
                    {s.convert_from_previous != null && (
                      <div className={`text-[11px] mt-0.5 font-semibold ${
                        s.convert_from_previous >= 50 ? "text-emerald-700" :
                        s.convert_from_previous >= 20 ? "text-amber-700"   :
                                                        "text-rose-700"
                      }`}>
                        ↓ {s.convert_from_previous}% from previous
                      </div>
                    )}
                    <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className={`h-full bg-gradient-to-r ${colour}`} style={{ width: `${widthPct}%` }}/>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 flex items-center justify-between flex-wrap gap-3 text-sm border-t border-slate-100 pt-3">
              <div className="text-slate-600">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Total revenue</span>
                <div className="text-xl font-heading font-bold text-slate-900 tabular-nums">
                  ₹{(acqFunnel.totals?.revenue_inr || 0).toLocaleString("en-IN")}
                </div>
              </div>
              <div className="text-xs text-slate-500">
                {acqFunnel.totals?.followup_booked || 0} follow-up bookings ·{" "}
                {acqFunnel.totals?.all_leads || 0} total leads ·{" "}
                window {acqFunnel.window_days}d
              </div>
            </div>
          </Card>
        )}

        {/* Doctor pending queue — the operational nudge dashboard */}
        {pendingQueue.length > 0 && (
          <Card className="mt-4 p-6 border-slate-200" data-testid="admin-pending-queue">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-600"/> Doctors with longest pending queue
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Ranked by oldest unreviewed paid case. Tap to call or WhatsApp the doctor a nudge.
                </p>
              </div>
              <span className="text-xs text-slate-500">{pendingQueue.length} doctor{pendingQueue.length !== 1 ? "s" : ""}</span>
            </div>
            <div className="mt-4 divide-y divide-slate-100">
              {pendingQueue.map((row) => (
                <div key={row.doctor_id} className="py-3 flex items-center gap-4 flex-wrap" data-testid={`pq-row-${row.doctor_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900 truncate">{row.doctor_name}</span>
                      {row.is_featured && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 font-semibold uppercase tracking-wider">Founder</span>}
                      {row.oldest_sla_breached && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200 font-semibold uppercase tracking-wider">SLA breached</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      <span className="font-mono text-slate-700">{row.pending_count}</span> pending
                      <span className="text-slate-300 mx-1.5">·</span>
                      oldest <span className="font-mono text-slate-700">{row.oldest_hours_open}h</span> (SLA {row.oldest_sla_hours}h)
                      <span className="text-slate-300 mx-1.5">·</span>
                      avg <span className="font-mono text-slate-700">{row.avg_age_hours}h</span>
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    {row.doctor_phone && (
                      <>
                        <a href={`tel:${row.doctor_phone}`} title="Call doctor">
                          <Button size="sm" variant="outline" className="h-8 px-2" data-testid={`pq-call-${row.doctor_id}`}>
                            <Phone className="w-3.5 h-3.5"/>
                          </Button>
                        </a>
                        <a
                          href={`https://wa.me/${String(row.doctor_phone).replace(/[^\d]/g,'')}?text=${encodeURIComponent(`Hi ${row.doctor_name || ''}, just a heads-up — your NeuroOpinion queue has ${row.pending_count} pending case(s), oldest ${row.oldest_hours_open}h. Could you take a look?`)}`}
                          target="_blank" rel="noreferrer" title="WhatsApp doctor"
                        >
                          <Button size="sm" variant="outline" className="h-8 px-2 border-green-300 text-green-700 hover:bg-green-50" data-testid={`pq-wa-${row.doctor_id}`}>
                            <MessageCircle className="w-3.5 h-3.5"/>
                          </Button>
                        </a>
                      </>
                    )}
                    <Link to={`/case/${row.oldest_case_id}`}>
                      <Button size="sm" variant="outline" className="h-8" data-testid={`pq-open-${row.doctor_id}`}>
                        Open oldest case
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {cards.map((c, i) => (
            <Card key={i} className="p-5 border-slate-200" data-testid={`stat-${c.label.toLowerCase().replace(/\s/g,"-")}`}>
              <div className={`w-9 h-9 rounded-lg grid place-items-center ${
                c.tone==="emerald" ? "bg-emerald-50 text-emerald-700"
                : c.tone==="amber" ? "bg-amber-50 text-amber-700"
                : c.tone==="teal"  ? "bg-teal-50 text-teal-700"
                : c.tone==="slate" ? "bg-slate-100 text-slate-700"
                : "bg-blue-50 text-blue-700"
              }`}>
                <c.icon className="w-5 h-5"/>
              </div>
              <div className="mt-3 text-xs uppercase tracking-wider text-slate-500">{c.label}</div>
              <div className="mt-1 font-heading text-2xl font-semibold text-slate-900">{c.value}</div>
            </Card>
          ))}
        </div>

        <Card className="mt-6 p-6 border-slate-200">
          <div className="font-heading text-lg font-semibold text-slate-900">Cases · last 7 days</div>
          <div className="h-64 mt-4" style={{ minHeight: 240 }}>
            <ResponsiveContainer width="100%" height="100%" minHeight={240}>
              <BarChart data={stats?.cases_series || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
                <XAxis dataKey="date" stroke="#64748B" fontSize={12}/>
                <YAxis stroke="#64748B" fontSize={12} allowDecimals={false}/>
                <Tooltip/>
                <Bar dataKey="count" fill="#1E40AF" radius={[6,6,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* All Cases — admin can view, search, filter, assign doctor */}
        <Card className="mt-6 p-6 border-slate-200" data-testid="admin-cases">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-700"/>
              <div className="font-heading text-lg font-semibold text-slate-900">All Cases</div>
              <span className="text-xs text-slate-500">({filteredCases.length} of {cases.length})</span>
              {botCaseCount > 0 && (
                <button
                  onClick={() => setCaseSource(caseSource === "whatsapp_bot_v3" ? "all" : "whatsapp_bot_v3")}
                  className={`ml-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border transition-colors ${caseSource === "whatsapp_bot_v3" ? "bg-emerald-600 text-white border-emerald-700" : "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"}`}
                  data-testid="admin-bot-cases-badge"
                  title="Click to filter WhatsApp-bot bookings only"
                >
                  🤖 {botCaseCount} WhatsApp bot
                </button>
              )}
              <button
                onClick={async () => {
                  try {
                    const r = await api.post("/admin/payments/reconcile-stuck");
                    const n = r.data?.confirmed?.length || 0;
                    if (n > 0) {
                      toast.success(`✅ Recovered ${n} stuck payment${n === 1 ? "" : "s"} from Cashfree.`);
                      // Refresh cases list
                      const fresh = await api.get("/admin/consultations");
                      setCases(fresh.data || []);
                    } else {
                      toast(`No stuck payments found. Checked ${r.data?.checked || 0} case${r.data?.checked === 1 ? "" : "s"}.`);
                    }
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Reconcile failed");
                  }
                }}
                className="ml-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 transition-colors"
                data-testid="admin-reconcile-btn"
                title="Ask Cashfree if any unpaid cases actually paid — auto-heals stuck browser redirects"
              >
                🔄 Reconcile Cashfree
              </button>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  className="pl-9 h-9 w-56"
                  placeholder="Search patient / doctor / case-id"
                  value={caseSearch}
                  onChange={(e) => setCaseSearch(e.target.value)}
                  data-testid="admin-cases-search"
                />
              </div>
              <Select value={caseSource} onValueChange={setCaseSource}>
                <SelectTrigger className="h-9 w-44" data-testid="admin-cases-source-filter">
                  <SelectValue/>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sources</SelectItem>
                  <SelectItem value="whatsapp_bot_v3">🤖 WhatsApp bot only</SelectItem>
                  <SelectItem value="web">🌐 Web/App only</SelectItem>
                </SelectContent>
              </Select>
              <Select value={caseStatus} onValueChange={setCaseStatus}>
                <SelectTrigger className="h-9 w-44" data-testid="admin-cases-status-filter">
                  <SelectValue/>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="pending_payment">Pending payment</SelectItem>
                  <SelectItem value="awaiting_review">Awaiting review</SelectItem>
                  <SelectItem value="under_review">Under review</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                </SelectContent>
              </Select>
              <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none" data-testid="admin-hide-refunded-toggle">
                <input type="checkbox" checked={hideRefunded} onChange={(e) => setHideRefunded(e.target.checked)} className="rounded"/>
                Hide refunded
              </label>
            </div>
          </div>

          <div className="mt-5 overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-cases-table">
              <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 pr-3 font-semibold">Patient</th>
                  <th className="text-left py-2 pr-3 font-semibold">Doctor</th>
                  <th className="text-left py-2 pr-3 font-semibold">Status</th>
                  <th className="text-left py-2 pr-3 font-semibold">Payment</th>
                  <th className="text-right py-2 pr-3 font-semibold">Amount</th>
                  <th className="text-right py-2 pr-3 font-semibold">Doctor share</th>
                  <th className="text-right py-2 pl-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredCases.slice(0, 100).map((c) => {
                  const st = STATUS_LABELS[c.status] || { text: c.status, cls: "bg-slate-100 text-slate-700 border-slate-200" };
                  const pay = PAY_LABELS[c.payment_status] || { text: c.payment_status, cls: "bg-slate-100 text-slate-600 border-slate-200" };
                  return (
                    <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-case-row-${c.id}`}>
                      <td className="py-3 pr-3">
                        <div className="font-semibold text-slate-900 flex items-center gap-1.5 flex-wrap">
                          {c.patient_name}
                          {c.source === "whatsapp_bot_v3" && (
                            <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" title={c.wa_bot_case_id || "WhatsApp bot booking"} data-testid={`admin-case-wa-badge-${c.id}`}>
                              🤖 WA Bot
                            </span>
                          )}
                        </div>
                        {c.patient_phone && <div className="text-xs text-slate-500">{c.patient_phone}</div>}
                        {c.wa_bot_case_id && <div className="text-[10px] font-mono text-slate-400">{c.wa_bot_case_id}</div>}
                      </td>
                      <td className="py-3 pr-3">
                        {assigningId === c.id ? (
                          <div className="flex items-center gap-2">
                            <Select onValueChange={(v) => assignDoctor(c.id, v)}>
                              <SelectTrigger className="h-9 w-48" data-testid={`assign-select-${c.id}`}>
                                <SelectValue placeholder="Choose doctor…"/>
                              </SelectTrigger>
                              <SelectContent>
                                {approvedDoctors.map((d) => (
                                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button size="sm" variant="ghost" onClick={() => setAssigningId(null)}>Cancel</Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="text-slate-700">{c.doctor_name}</span>
                            <button
                              onClick={() => setAssigningId(c.id)}
                              className="text-xs text-blue-700 hover:underline inline-flex items-center gap-0.5"
                              data-testid={`assign-btn-${c.id}`}
                            >
                              <UserCheck className="w-3 h-3"/> Reassign
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="py-3 pr-3">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${st.cls}`}>{st.text}</span>
                      </td>
                      <td className="py-3 pr-3">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${pay.cls}`}>{pay.text}</span>
                      </td>
                      <td className="py-3 pr-3 text-right tabular-nums">
                        ₹{(c.amount || 0).toLocaleString()}
                      </td>
                      <td className="py-3 pr-3 text-right tabular-nums text-emerald-700 font-semibold">
                        ₹{(c.doctor_share || 0).toLocaleString()}
                      </td>
                      <td className="py-3 pl-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <Link
                            to={`/case/${c.id}`}
                            className="text-xs font-semibold text-blue-700 hover:underline"
                            data-testid={`view-case-${c.id}`}
                          >View →</Link>
                          {c.status !== "completed" && c.status !== "rejected" && c.status !== "refunded" && (
                            <button
                              onClick={() => setRejectFor(c)}
                              className="text-xs font-semibold text-rose-700 hover:underline inline-flex items-center gap-0.5"
                              data-testid={`admin-reject-case-${c.id}`}
                              title="Reject & auto-refund"
                            >
                              <X className="w-3 h-3"/> Reject
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredCases.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-10 text-center text-sm text-slate-500">No cases match your filters.</td>
                  </tr>
                )}
              </tbody>
            </table>
            {filteredCases.length > 100 && (
              <div className="text-xs text-slate-500 text-center mt-3">Showing first 100 of {filteredCases.length} cases. Refine your search to see more.</div>
            )}
          </div>
        </Card>

        {/* Doctor Payouts — per-doctor monthly breakdown */}
        <Card className="mt-6 p-6 border-slate-200" data-testid="admin-payouts">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-emerald-700"/>
              <div className="font-heading text-lg font-semibold text-slate-900">Doctor Payouts (per month)</div>
              <span className="text-xs text-slate-500">Doctor share = 70% of paid case revenue</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={exportPayoutsCSV}
                disabled={!(payouts.items || []).length}
                data-testid="payout-export-csv-btn"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              >
                <Download className="w-4 h-4 mr-1.5"/> Export CSV
              </Button>
              <Select value={payoutMonth || "all"} onValueChange={(v) => setPayoutMonth(v === "all" ? "" : v)}>
                <SelectTrigger className="h-9 w-44" data-testid="payout-month-filter">
                  <SelectValue placeholder="All months"/>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All months</SelectItem>
                  {(payouts.months || []).map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Per-month totals — quick snapshot */}
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {(() => {
              const months = {};
              (payouts.items || []).forEach(it => {
                if (!months[it.month]) months[it.month] = { gross: 0, share: 0, cases: 0 };
                months[it.month].gross += it.gross;
                months[it.month].share += it.doctor_share;
                months[it.month].cases += it.cases;
              });
              const sortedMonths = Object.keys(months).sort().reverse().slice(0, 4);
              return sortedMonths.map((m) => (
                <div key={m} className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">{m}</div>
                  <div className="font-heading text-lg font-bold text-slate-900 mt-1 tabular-nums">
                    ₹{months[m].share.toLocaleString()}
                  </div>
                  <div className="text-[11px] text-slate-600">
                    {months[m].cases} cases · ₹{months[m].gross.toLocaleString()} gross
                  </div>
                </div>
              ));
            })()}
          </div>

          {/* Per-doctor per-month table */}
          <div className="mt-5 overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-payouts-table">
              <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 pr-3 font-semibold">Month</th>
                  <th className="text-left py-2 pr-3 font-semibold">Doctor</th>
                  <th className="text-right py-2 pr-3 font-semibold">Cases</th>
                  <th className="text-right py-2 pr-3 font-semibold">Completed</th>
                  <th className="text-right py-2 pr-3 font-semibold">Gross revenue</th>
                  <th className="text-right py-2 pr-3 font-semibold">Doctor share (70%)</th>
                  <th className="text-right py-2 pr-3 font-semibold">Platform (30%)</th>
                  <th className="text-center py-2 pl-3 font-semibold">Payout status</th>
                </tr>
              </thead>
              <tbody>
                {(payouts.items || []).map((p, i) => (
                  <tr key={`${p.month}-${p.doctor_id}-${i}`}
                      className={`border-b border-slate-100 hover:bg-slate-50 ${p.settled ? "bg-emerald-50/30" : ""}`}
                      data-testid={`payout-row-${i}`}>
                    <td className="py-3 pr-3 font-mono text-xs text-slate-600">{p.month}</td>
                    <td className="py-3 pr-3">
                      <div className="font-semibold text-slate-900">{p.doctor_name}</div>
                      {p.specialization && <div className="text-xs text-slate-500">{p.specialization}</div>}
                    </td>
                    <td className="py-3 pr-3 text-right tabular-nums">{p.cases}</td>
                    <td className="py-3 pr-3 text-right tabular-nums text-emerald-700 font-semibold">{p.completed}</td>
                    <td className="py-3 pr-3 text-right tabular-nums">₹{(p.gross || 0).toLocaleString()}</td>
                    <td className="py-3 pr-3 text-right tabular-nums text-emerald-700 font-bold">₹{(p.doctor_share || 0).toLocaleString()}</td>
                    <td className="py-3 pr-3 text-right tabular-nums text-slate-500">₹{(p.platform_share || 0).toLocaleString()}</td>
                    <td className="py-3 pl-3 text-center">
                      {p.settled ? (
                        <div className="flex items-center justify-center gap-2">
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300">
                            <BadgeCheck className="w-3 h-3"/> Paid
                          </span>
                          <button
                            onClick={() => settlePayout(p.doctor_id, p.month, false)}
                            className="text-slate-400 hover:text-rose-600 p-1 rounded"
                            title="Mark as unpaid"
                            data-testid={`payout-revert-${i}`}
                          >
                            <RotateCcw className="w-3.5 h-3.5"/>
                          </button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!p.doctor_id || (p.gross || 0) === 0}
                          onClick={() => settlePayout(p.doctor_id, p.month, true)}
                          className="h-8 px-3 border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs"
                          data-testid={`payout-settle-${i}`}
                        >
                          Mark as paid
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {(payouts.items || []).length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-10 text-center text-sm text-slate-500">No paid cases yet for this period.</td>
                  </tr>
                )}
              </tbody>
              {(payouts.items || []).length > 0 && (
                <tfoot className="border-t-2 border-slate-300">
                  <tr className="font-bold text-slate-900">
                    <td className="py-3 pr-3" colSpan={2}>TOTAL</td>
                    <td className="py-3 pr-3 text-right tabular-nums">{payouts.items.reduce((s, p) => s + (p.cases || 0), 0)}</td>
                    <td className="py-3 pr-3 text-right tabular-nums">{payouts.items.reduce((s, p) => s + (p.completed || 0), 0)}</td>
                    <td className="py-3 pr-3 text-right tabular-nums">₹{payouts.items.reduce((s, p) => s + (p.gross || 0), 0).toLocaleString()}</td>
                    <td className="py-3 pr-3 text-right tabular-nums text-emerald-700">₹{payouts.items.reduce((s, p) => s + (p.doctor_share || 0), 0).toLocaleString()}</td>
                    <td className="py-3 pr-3 text-right tabular-nums text-slate-500">₹{payouts.items.reduce((s, p) => s + (p.platform_share || 0), 0).toLocaleString()}</td>
                    <td className="py-3 pl-3 text-center text-[11px] text-slate-500 font-normal">
                      {payouts.items.filter(p => p.settled).length} of {payouts.items.length} settled
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Card>
        <Card className="mt-6 p-6 border-slate-200" data-testid="admin-funnel">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-700"/>
              <div className="font-heading text-lg font-semibold text-slate-900">Conversion funnel</div>
            </div>
            <div className="text-xs text-slate-500">Signup → completed opinion <span className="font-semibold text-blue-700">{funnel?.end_to_end_conversion_pct ?? 0}%</span></div>
          </div>
          <div className="mt-5 space-y-3">
            {(funnel?.stages || []).map((s, i) => {
              const top = funnel.stages[0]?.count || 1;
              const widthPct = Math.max(6, Math.round((s.count / top) * 100));
              return (
                <div key={s.key} data-testid={`funnel-${s.key}`}>
                  <div className="flex items-baseline justify-between text-sm">
                    <div className="font-medium text-slate-900">{i + 1}. {s.label}</div>
                    <div className="text-slate-600">
                      <span className="font-heading font-semibold text-slate-900">{s.count}</span>
                      {i > 0 && <span className="ml-2 text-xs text-slate-500">({s.conversion_from_prev}% from prev)</span>}
                    </div>
                  </div>
                  <div className="mt-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-blue-600 to-blue-500 transition-all" style={{ width: `${widthPct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          {funnel?.refunded > 0 && (
            <div className="mt-4 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              ⚠ {funnel.refunded} refunded case{funnel.refunded > 1 ? "s" : ""} (SLA breaches auto-processed)
            </div>
          )}
        </Card>

        {/* AI Models */}
        <Card className="mt-6 p-6 border-slate-200" data-testid="admin-ai-models">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-700"/>
              <div className="font-heading text-lg font-semibold text-slate-900">AI models — auto-updating</div>
            </div>
            <Button size="sm" variant="outline" disabled={aiChecking} onClick={checkAiUpgrade} data-testid="ai-upgrade-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${aiChecking ? "animate-spin" : ""}`}/>
              {aiChecking ? "Probing…" : "Check for newer version"}
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-1">The system probes a try-ladder of model names on every startup and when you click the button above. It silently upgrades when a newer one is accepted — no redeploy needed.</p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {(aiStatus?.providers || []).map(p => (
              <div key={p.provider} className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid={`ai-provider-${p.provider}`}>
                <div className="flex items-center justify-between">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{p.provider === "openai" ? "OpenAI · ChatGPT" : "Anthropic · Claude"}</div>
                  {p.on_newest
                    ? <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Latest</span>
                    : <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Newer candidate: {p.newest_candidate}</span>
                  }
                </div>
                <div className="mt-2 font-mono text-sm text-slate-900 break-all">{p.active}</div>
                <div className="mt-1 text-[11px] text-slate-500">
                  {p.source === "auto-upgrade" ? "Auto-selected" : p.source} · {p.updated_at ? `last checked ${new Date(p.updated_at).toLocaleString()}` : "not yet checked"}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <h2 className="font-heading text-2xl font-semibold text-slate-900 mt-10">{t("pending_approvals")}</h2>

        {/* Doctor onboarding nudge — fires a WhatsApp + email to every
            approved doctor with an incomplete profile. Idempotent per-doctor. */}
        <OnboardingNudgeButton />

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {pending.length === 0 && <div className="text-sm text-slate-500">No pending doctors.</div>}
          {pending.map(d => (
            <Card key={d.id} className="p-5 border-slate-200" data-testid={`pending-${d.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-heading font-semibold text-slate-900">{d.name}</div>
                  <div className="text-xs text-slate-500">{d.email} · {d.phone || "no phone"}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">Applied {new Date(d.created_at).toLocaleString()}</div>
                </div>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Pending</span>
              </div>
              <div className="mt-3 text-sm text-slate-700 space-y-1">
                <div><span className="text-slate-500">Specialization:</span> {d.specialization}</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-slate-500">Reg No:</span>
                  <span className="font-mono font-semibold text-slate-900">{d.registration_number || "—"}</span>
                  {d.registration_number && (
                    <a
                      href={`https://nmr.nmc.org.in/Home/SearchDoctor?Reg_No=${encodeURIComponent(d.registration_number)}`}
                      target="_blank" rel="noreferrer"
                      className="text-[11px] font-semibold text-blue-700 hover:underline"
                      data-testid={`nmc-verify-${d.id}`}
                    >
                      Verify on NMC ↗
                    </a>
                  )}
                </div>
                <div><span className="text-slate-500">Experience:</span> {d.experience_years || 0} yrs</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-slate-500">License:</span>
                  {d.license_file_id ? (
                    <a
                      href={`${API_BASE}/files/${d.license_file_id}/download?auth=${getToken() || ""}`}
                      target="_blank" rel="noreferrer"
                      className="text-[11px] font-semibold text-blue-700 hover:underline"
                      data-testid={`license-view-${d.id}`}
                    >
                      View uploaded file ↗
                    </a>
                  ) : (
                    <span className="text-[11px] text-rose-700 font-semibold">⚠ not uploaded</span>
                  )}
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <Button size="sm" onClick={() => decide(d.id, true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-${d.id}`}>
                  Approve
                </Button>
                <Button size="sm" variant="outline" onClick={() => decide(d.id, false)} className="border-red-300 text-red-700 hover:bg-red-50" data-testid={`reject-${d.id}`}>
                  Reject
                </Button>
                <Button size="sm" variant="ghost"
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/doctors/${d.id}`);
                          toast.success("Profile URL copied");
                        }}>
                  Copy profile URL
                </Button>
              </div>
            </Card>
          ))}
        </div>

        <h2 className="font-heading text-2xl font-semibold text-slate-900 mt-10">All doctors</h2>
        <Card className="mt-4 p-0 border-slate-200 overflow-hidden" data-testid="admin-all-doctors">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Name</th>
                  <th className="text-left px-4 py-3 font-semibold">Specialization</th>
                  <th className="text-left px-4 py-3 font-semibold">Reg No</th>
                  <th className="text-left px-4 py-3 font-semibold">License</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-right px-4 py-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {all.map(d => (
                  <tr key={d.id} className="border-t border-slate-200" data-testid={`all-doctor-${d.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      <div>{d.name}</div>
                      <div className="text-[10px] text-slate-500 font-normal">{d.email}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{d.specialization || "—"}</td>
                    <td className="px-4 py-3">
                      {editingRegId === d.id ? (
                        <div className="flex items-center gap-1.5 flex-wrap" data-testid={`reg-edit-row-${d.id}`}>
                          <input
                            type="text"
                            value={regDraft}
                            onChange={(e) => setRegDraft(e.target.value.toUpperCase())}
                            placeholder="TSMC-XXXX-XXXX"
                            className="h-8 w-32 rounded border border-slate-300 px-2 text-[11px] font-mono uppercase focus:outline-none focus:ring-2 focus:ring-blue-500"
                            data-testid={`reg-input-${d.id}`}
                            autoFocus
                            maxLength={40}
                            onKeyDown={(e) => { if (e.key === "Enter") saveDoctorReg(d.id); if (e.key === "Escape") cancelEditReg(); }}
                          />
                          <Button size="sm" onClick={() => saveDoctorReg(d.id)} disabled={regSaving} className="h-7 px-2 text-[11px] bg-blue-700 hover:bg-blue-800" data-testid={`reg-save-${d.id}`}>
                            {regSaving ? "…" : "Save"}
                          </Button>
                          <button type="button" onClick={cancelEditReg} className="h-7 px-1.5 text-[11px] text-slate-500 hover:text-slate-800" data-testid={`reg-cancel-${d.id}`}>✕</button>
                        </div>
                      ) : d.registration_number ? (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-mono text-[11px] font-semibold text-slate-900">{d.registration_number}</span>
                          {d.registration_verified && (
                            <BadgeCheck className="w-3.5 h-3.5 text-emerald-600" title="Verified"/>
                          )}
                          <button
                            type="button"
                            onClick={() => startEditReg(d)}
                            className="text-[10px] font-semibold text-blue-700 hover:underline ml-1"
                            data-testid={`reg-edit-${d.id}`}
                          >Edit</button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => startEditReg(d)}
                          className="text-[11px] font-semibold text-rose-600 hover:text-blue-700 hover:underline inline-flex items-center gap-1"
                          data-testid={`reg-add-${d.id}`}
                        >
                          ⚠ add reg no
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {d.license_file_id ? (
                        <a
                          href={`${API_BASE}/files/${d.license_file_id}/download?auth=${getToken() || ""}`}
                          target="_blank" rel="noreferrer"
                          className="text-[11px] font-semibold text-blue-700 hover:underline inline-flex items-center gap-1"
                          data-testid={`all-doc-license-${d.id}`}
                        >
                          <FileText className="w-3 h-3"/> View
                        </a>
                      ) : <span className="text-[11px] text-rose-600 font-semibold">⚠ not uploaded</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${d.status==="approved"?"bg-emerald-50 text-emerald-700 border-emerald-200":d.status==="rejected"?"bg-red-50 text-red-700 border-red-200":"bg-amber-50 text-amber-700 border-amber-200"}`}>{d.status}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1.5 flex-wrap justify-end">
                        {d.status !== "approved" && (
                          <Button size="sm" onClick={() => decide(d.id, true)} className="h-7 px-2.5 text-[11px] bg-emerald-600 hover:bg-emerald-700" data-testid={`all-approve-${d.id}`}>
                            Approve
                          </Button>
                        )}
                        {d.status !== "rejected" && (
                          <Button size="sm" variant="outline" onClick={() => decide(d.id, false)} className="h-7 px-2.5 text-[11px] border-red-300 text-red-700 hover:bg-red-50" data-testid={`all-reject-${d.id}`}>
                            Reject
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {all.length === 0 && (
                  <tr><td colSpan={6} className="py-10 text-center text-sm text-slate-500">No doctors yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <h2 className="font-heading text-2xl font-semibold text-slate-900 mt-10">All cases</h2>
        {(() => {
          const urgent = cases.filter((c) => (c.service_type === "EMERGENCY" || c.priority === "urgent") && c.payment_status === "paid" && c.status !== "completed");
          if (urgent.length === 0) return null;
          return (
            <div data-testid="urgent-emergency-banner" className="mt-4 rounded-xl border border-red-300 bg-red-50 p-4 flex items-start gap-3 shadow-sm">
              <span className="text-2xl">🚨</span>
              <div className="flex-1">
                <div className="font-heading font-bold text-red-800">
                  {urgent.length} Priority or Video Consultation case{urgent.length > 1 ? "s" : ""} awaiting review
                </div>
                <div className="text-xs text-red-700 mt-0.5">
                  These patients paid the priority fee and are on the &lt;24h SLA. Assign a specialist ASAP.
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {urgent.slice(0, 6).map((c) => (
                    <span key={c.id} className="text-[11px] font-medium bg-white border border-red-200 text-red-800 rounded-full px-2 py-0.5">
                      {c.patient_name} · {c.currency === "USD" ? `$${c.fee}` : `₹${c.fee}`}
                    </span>
                  ))}
                  {urgent.length > 6 && <span className="text-[11px] text-red-700">+{urgent.length - 6} more</span>}
                </div>
              </div>
            </div>
          );
        })()}
        <Card className="mt-4 p-0 border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Patient</th>
                  <th className="text-left px-4 py-3 font-semibold">Doctor</th>
                  <th className="text-left px-4 py-3 font-semibold">Fee</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-left px-4 py-3 font-semibold">Date</th>
                  <th className="text-left px-4 py-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {cases.map(c => {
                  const isIntl = (c.currency === "USD") || (c.country_code && c.country_code !== "IN");
                  const isStuck = ["rejected", "pending_payment"].includes(c.status) && c.payment_status !== "paid";
                  const canRetry = isIntl && isStuck;
                  const isEmergency = (c.service_type === "EMERGENCY") || c.priority === "urgent";
                  // Live SLA countdown (Emergency = 24 hrs, Regular = 48 hrs).
                  // Starts from paid_at if available, else created_at.
                  const isActive = isEmergency && c.payment_status === "paid" && c.status !== "completed";
                  let slaLabel = null, slaTone = "";
                  if (isActive) {
                    const start = new Date(c.paid_at || c.created_at).getTime();
                    const deadline = start + 24 * 3600 * 1000;
                    const remainingMs = deadline - Date.now();
                    if (remainingMs <= 0) {
                      slaLabel = "SLA BREACHED";
                      slaTone = "bg-red-700 text-white";
                    } else {
                      const h = Math.floor(remainingMs / 3600000);
                      const m = Math.floor((remainingMs % 3600000) / 60000);
                      slaLabel = `${h}h ${m}m left`;
                      slaTone = remainingMs < 3600000 ? "bg-red-600 text-white animate-pulse"
                              : remainingMs < 6 * 3600000 ? "bg-amber-500 text-white"
                              : "bg-emerald-600 text-white";
                    }
                  }
                  return (
                  <tr key={c.id} className={`border-t border-slate-200 ${isEmergency && c.payment_status === "paid" && c.status !== "completed" ? "bg-red-50/40" : ""}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {c.patient_name}
                        {isEmergency && (
                          <span data-testid={`emergency-badge-${c.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-red-600 text-white animate-pulse">
                            🚨 URGENT
                          </span>
                        )}
                        {slaLabel && (
                          <span data-testid={`sla-timer-${c.id}`} className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${slaTone}`}>
                            ⏱ {slaLabel}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">{c.doctor_name}</td>
                    <td className="px-4 py-3" data-testid={`admin-case-fee-${c.id}`}>{c.currency === "USD" ? `$${c.fee || 0} USD` : `₹${c.fee || 0}`}</td>
                    <td className="px-4 py-3">{c.status}</td>
                    <td className="px-4 py-3 text-slate-500">{new Date(c.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      {canRetry && (
                        <button
                          onClick={() => retryAsUSD(c)}
                          data-testid={`admin-retry-usd-${c.id}`}
                          className="text-xs font-semibold px-2.5 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white"
                          title="Generate a new PayPal USD payment link"
                        >Retry as USD</button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Audit log */}
        <h2 className="font-heading text-2xl font-semibold text-slate-900 mt-10 flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-blue-700"/> Audit log
        </h2>
        <p className="text-sm text-slate-500 mt-1">Security-relevant events: admin logins, doctor approvals, auto-refunds. Last 50 entries.</p>
        <Card className="mt-4 p-0 border-slate-200 overflow-hidden" data-testid="admin-audit">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">When</th>
                  <th className="text-left px-4 py-3 font-semibold">Action</th>
                  <th className="text-left px-4 py-3 font-semibold">Actor</th>
                  <th className="text-left px-4 py-3 font-semibold">Target</th>
                  <th className="text-left px-4 py-3 font-semibold">IP</th>
                </tr>
              </thead>
              <tbody>
                {audit.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-6 text-sm text-slate-500 text-center">No audit events yet.</td></tr>
                )}
                {audit.map(a => (
                  <tr key={a.id} className="border-t border-slate-200" data-testid={`audit-row-${a.id}`}>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-800 border border-slate-200">{a.action}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{a.actor_email || <span className="text-slate-400">system</span>}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {a.target_type ? `${a.target_type}:${(a.target_id || "").slice(0,8)}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-500 font-mono text-xs">{a.ip || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Patient reviews — moderation */}
        <AdminCoupons />
        <h2 className="font-heading text-2xl font-semibold text-slate-900 mt-10 flex items-center gap-2" data-testid="admin-reviews-section">
          <Sparkles className="w-5 h-5 text-amber-600"/> Patient reviews
        </h2>
        <p className="text-sm text-slate-500 mt-1">Latest reviews submitted by patients. Hide spam / off-topic content from the public site, or delete permanently.</p>
        <Card className="mt-3 p-0 overflow-hidden border-slate-200">
          {reviews.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="admin-reviews-empty">No reviews yet.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {reviews.map((r) => {
                const ratingColour = r.rating >= 4 ? "text-emerald-600" : r.rating >= 3 ? "text-amber-600" : "text-rose-600";
                return (
                  <div key={r.id} className="p-4 flex items-start gap-4 flex-wrap" data-testid={`admin-review-${r.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-heading text-lg font-semibold ${ratingColour}`}>{"★".repeat(r.rating)}<span className="text-slate-200">{"★".repeat(5 - r.rating)}</span></span>
                        <span className="text-xs font-semibold text-slate-700">{r.rating}/5</span>
                        {r.publish_publicly ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 font-semibold uppercase tracking-wider">Public</span>
                        ) : (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 font-semibold uppercase tracking-wider">Hidden</span>
                        )}
                      </div>
                      {r.text && (
                        <blockquote className="mt-2 text-sm text-slate-700 italic">“{r.text}”</blockquote>
                      )}
                      <div className="text-[11px] text-slate-500 mt-1.5">
                        <span className="font-medium text-slate-700">{r.patient_real_name || r.patient_name_anon}</span>
                        {r.patient_email ? <span className="ml-1">· <a className="text-blue-700 hover:underline" href={`mailto:${r.patient_email}`}>{r.patient_email}</a></span> : null}
                        {" · "}
                        about <span className="font-medium text-slate-700">{r.doctor_name}</span>
                        {" · "}
                        <span className="font-mono text-slate-400">{new Date(r.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleReviewVisibility(r.id, !r.publish_publicly, r.patient_name_anon)}
                        data-testid={`admin-review-toggle-${r.id}`}
                      >
                        {r.publish_publicly ? "Hide from public" : "Show on public site"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteReview(r.id)}
                        className="border-rose-300 text-rose-700 hover:bg-rose-50"
                        data-testid={`admin-review-delete-${r.id}`}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Hospital partners — Phase 5 B2B */}
        <h2 className="font-heading text-2xl font-semibold text-slate-900 mt-10 flex items-center gap-2" data-testid="admin-hospitals-section">
          <UserCheck className="w-5 h-5 text-blue-700"/> Hospital partners
        </h2>
        <p className="text-sm text-slate-500 mt-1">B2B referral partners. Approve them to unlock the hospital dashboard at <span className="font-mono">/hospital</span>.</p>
        <div className="mt-3 flex gap-2 flex-wrap" data-testid="hosp-filter-bar">
          {["pending", "approved", "rejected", "all"].map((s) => (
            <button
              key={s}
              onClick={() => setHospFilter(s)}
              data-testid={`hosp-filter-${s}`}
              className={`h-8 px-3 rounded-full text-xs font-semibold border transition ${
                hospFilter === s
                  ? "bg-blue-700 text-white border-blue-700"
                  : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
              }`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)} <span className="ml-1 opacity-70">{s === "all" ? hospitals.length : (hospitals.filter(h => h.status === s).length)}</span>
            </button>
          ))}
        </div>
        <Card className="mt-3 p-0 overflow-hidden border-slate-200">
          {hospitals.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="hosp-empty">No hospital applications in this view yet.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {hospitals.map((h) => {
                const pillColour = {
                  pending:  "bg-amber-100 text-amber-800 border-amber-200",
                  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
                  rejected: "bg-rose-100 text-rose-800 border-rose-200",
                }[h.status] || "bg-slate-100 text-slate-700 border-slate-200";
                return (
                  <div key={h.id} className="p-4 flex items-start gap-4 flex-wrap" data-testid={`hosp-row-${h.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-slate-900 truncate">{h.organization_name || h.name}</div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-semibold uppercase tracking-wider ${pillColour}`}>{h.status}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {h.organization_city ? `${h.organization_city}, ` : ""}{h.organization_state || ""}
                        {h.organization_address ? ` · ${h.organization_address}` : ""}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        Contact: <span className="font-medium text-slate-700">{h.name}</span>
                        {h.contact_designation ? ` (${h.contact_designation})` : ""} · <a className="text-blue-700 hover:underline" href={`mailto:${h.email}`}>{h.email}</a>
                        {h.phone || h.organization_phone ? ` · ${h.phone || h.organization_phone}` : ""}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5 font-mono">applied {new Date(h.created_at).toLocaleString()}</div>
                    </div>
                    {h.status === "pending" && (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => decideHospital(h.id, true, h.organization_name)}
                                className="bg-emerald-600 hover:bg-emerald-700" data-testid={`hosp-approve-${h.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1"/> Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => decideHospital(h.id, false, h.organization_name)}
                                className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`hosp-reject-${h.id}`}>
                          <X className="w-3.5 h-3.5 mr-1"/> Reject
                        </Button>
                      </div>
                    )}
                    {h.status === "approved" && h.organization_phone && (
                      <a href={`https://wa.me/${String(h.organization_phone).replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer">
                        <Button size="sm" variant="outline" className="border-green-300 text-green-700 hover:bg-green-50">
                          <MessageCircle className="w-3.5 h-3.5 mr-1"/> WhatsApp
                        </Button>
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Callback leads — Max-style "Request a Callback" submissions */}
        <h2 className="font-heading text-2xl font-semibold text-slate-900 mt-10 flex items-center gap-2">
          <Phone className="w-5 h-5 text-teal-700"/> Callback requests
        </h2>
        <p className="text-sm text-slate-500 mt-1">Leads from the &ldquo;Request a Call back&rdquo; widget on the marketing pages &amp; blog.</p>
        <div className="mt-3 flex gap-2 flex-wrap" data-testid="leads-filter-bar">
          {["new","contacted","converted","discarded","all"].map((s) => (
            <button
              key={s}
              onClick={() => setLeadsFilter(s)}
              className={`h-8 px-3 rounded-full text-xs font-semibold border transition ${
                leadsFilter === s
                  ? "bg-teal-700 text-white border-teal-700"
                  : "bg-white text-slate-700 border-slate-200 hover:border-teal-400"
              }`}
              data-testid={`leads-filter-${s}`}
            >
              {s[0].toUpperCase()+s.slice(1)}
            </button>
          ))}
        </div>
        <Card className="mt-4 p-0 border-slate-200 overflow-hidden" data-testid="admin-leads">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">When</th>
                  <th className="text-left px-4 py-3 font-semibold">Name</th>
                  <th className="text-left px-4 py-3 font-semibold">Phone</th>
                  <th className="text-left px-4 py-3 font-semibold">Query</th>
                  <th className="text-left px-4 py-3 font-semibold">Source</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-left px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-6 text-sm text-slate-500 text-center">No {leadsFilter === "all" ? "" : leadsFilter} leads.</td></tr>
                )}
                {leads.map(l => (
                  <React.Fragment key={l.id}>
                  <tr className="border-t border-slate-200" data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">{new Date(l.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3 font-semibold text-slate-800">
                      <div>{l.name}</div>
                      {(l.city || l.city_from_ip) && (
                        <div className="text-[10px] text-slate-500 font-normal flex items-center gap-1" data-testid={`lead-city-${l.id}`}>
                          📍 {l.city || `${l.city_from_ip || ""}${l.region_from_ip ? ", " + l.region_from_ip : ""}`}{!l.city && l.city_from_ip && <span className="text-slate-400">(IP)</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-0.5">
                        {l.phone ? (
                          <div className="flex items-center gap-2">
                            <a href={`tel:${l.phone}`} className="text-blue-700 font-mono text-xs hover:underline">{l.phone}</a>
                            <a href={`https://wa.me/${String(l.whatsapp || l.phone).replace(/[^\d]/g,'')}`} target="_blank" rel="noreferrer" className="text-green-600 hover:text-green-700" data-testid={`lead-wa-${l.id}`}>
                              <MessageCircle className="w-4 h-4"/>
                            </a>
                          </div>
                        ) : (
                          <span className="text-slate-400 text-xs italic">no phone</span>
                        )}
                        {l.whatsapp && l.whatsapp !== l.phone && (
                          <a href={`https://wa.me/${String(l.whatsapp).replace(/[^\d]/g,'')}`} target="_blank" rel="noreferrer" className="text-[10px] text-green-700 font-mono hover:underline" data-testid={`lead-wa-num-${l.id}`}>
                            wa: {l.whatsapp}
                          </a>
                        )}
                        {l.email && (
                          <a href={`mailto:${l.email}`} className="text-[10px] text-slate-600 hover:underline truncate max-w-[180px]" data-testid={`lead-email-${l.id}`} title={l.email}>
                            ✉ {l.email}
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 max-w-[280px] truncate" title={l.query}>{l.query || "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      <div className="flex items-center gap-1.5">
                        {isWaLead(l) && (
                          <span className="inline-flex items-center gap-0.5 bg-green-100 text-green-800 text-[10px] font-bold px-1.5 py-0.5 rounded-full" title="WhatsApp-generated lead" data-testid={`lead-wa-badge-${l.id}`}>
                            <MessageCircle className="w-2.5 h-2.5"/> WA
                          </span>
                        )}
                        <span>{l.source || "—"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
                        l.status === "new" ? "bg-blue-50 text-blue-700 border-blue-200" :
                        l.status === "contacted" ? "bg-amber-50 text-amber-700 border-amber-200" :
                        l.status === "converted" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                        "bg-slate-50 text-slate-600 border-slate-200"
                      }`}>{l.status}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 flex-wrap">
                        {waNumber(l) && (
                          <Button
                            size="sm"
                            onClick={() => openWaChat(l)}
                            data-testid={`lead-wa-chat-${l.id}`}
                            className="h-7 px-2.5 text-[11px] bg-green-600 hover:bg-green-700 text-white inline-flex items-center gap-1"
                            title={`Open WhatsApp chat with prefilled greeting → ${l.whatsapp || l.phone}`}
                          >
                            <MessageCircle className="w-3 h-3"/> WhatsApp
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => startEditLead(l)} data-testid={`lead-edit-${l.id}`} className="h-7 px-2 text-[11px]">Edit</Button>
                        {!l.city_from_ip && !l.city && l.ip && (
                          <Button size="sm" variant="outline" onClick={() => resolveCity(l.id)} disabled={cityResolving === l.id} data-testid={`lead-city-resolve-${l.id}`} className="h-7 px-2 text-[11px] text-blue-700 border-blue-300 hover:bg-blue-50">
                            {cityResolving === l.id ? "…" : "📍 City"}
                          </Button>
                        )}
                        {l.status !== "contacted" && (
                          <Button size="sm" variant="outline" onClick={() => updateLeadStatus(l.id, "contacted")} data-testid={`lead-contacted-${l.id}`} className="h-7 px-2 text-[11px]">Contacted</Button>
                        )}
                        {l.status !== "converted" && (
                          <Button size="sm" variant="outline" onClick={() => updateLeadStatus(l.id, "converted")} data-testid={`lead-converted-${l.id}`} className="h-7 px-2 text-[11px] text-emerald-700 border-emerald-300 hover:bg-emerald-50">Converted</Button>
                        )}
                        {l.status !== "discarded" && (
                          <Button size="sm" variant="outline" onClick={() => updateLeadStatus(l.id, "discarded")} data-testid={`lead-discarded-${l.id}`} className="h-7 px-2 text-[11px] text-slate-500 border-slate-200 hover:bg-slate-50">Discard</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {editLeadId === l.id && (
                    <tr className="bg-blue-50/40 border-t border-blue-200" data-testid={`lead-edit-row-${l.id}`}>
                      <td colSpan={7} className="px-4 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                          <label className="text-[11px] font-semibold text-slate-600">
                            Phone
                            <input type="text" value={leadDraft.phone} onChange={(e) => setLeadDraft({ ...leadDraft, phone: e.target.value })}
                                   placeholder="+91…" className="mt-1 w-full h-9 rounded border border-slate-300 px-2 text-sm font-mono"
                                   data-testid={`lead-edit-phone-${l.id}`} maxLength={20}/>
                          </label>
                          <label className="text-[11px] font-semibold text-slate-600">
                            WhatsApp
                            <input type="text" value={leadDraft.whatsapp} onChange={(e) => setLeadDraft({ ...leadDraft, whatsapp: e.target.value })}
                                   placeholder="(if different)" className="mt-1 w-full h-9 rounded border border-slate-300 px-2 text-sm font-mono"
                                   data-testid={`lead-edit-whatsapp-${l.id}`} maxLength={20}/>
                          </label>
                          <label className="text-[11px] font-semibold text-slate-600">
                            Email
                            <input type="email" value={leadDraft.email} onChange={(e) => setLeadDraft({ ...leadDraft, email: e.target.value })}
                                   placeholder="patient@…" className="mt-1 w-full h-9 rounded border border-slate-300 px-2 text-sm"
                                   data-testid={`lead-edit-email-${l.id}`} maxLength={120}/>
                          </label>
                          <label className="text-[11px] font-semibold text-slate-600 md:col-span-1">
                            Admin notes
                            <input type="text" value={leadDraft.admin_notes} onChange={(e) => setLeadDraft({ ...leadDraft, admin_notes: e.target.value })}
                                   placeholder="Internal notes (private)" className="mt-1 w-full h-9 rounded border border-slate-300 px-2 text-sm"
                                   data-testid={`lead-edit-notes-${l.id}`} maxLength={600}/>
                          </label>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <Button size="sm" onClick={saveLead} disabled={leadSaving} className="h-8 px-3 text-xs bg-blue-700 hover:bg-blue-800" data-testid={`lead-edit-save-${l.id}`}>
                            {leadSaving ? "Saving…" : "Save"}
                          </Button>
                          <Button size="sm" variant="outline" onClick={cancelEditLead} className="h-8 px-3 text-xs" data-testid={`lead-edit-cancel-${l.id}`}>Cancel</Button>
                          {l.admin_notes && !editLeadId && (
                            <span className="text-[10px] text-slate-500 italic">Last note: {l.admin_notes}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Meta Lead Ads — leads captured via Facebook / Instagram Lead Ad forms */}
        <MetaLeadsSection />

        {/* Referral leaderboard — top P2P advocates for personal thank-yous */}
        <div className="mt-10" id="admin-referral-leaderboard">
          <ReferralLeaderboard />
        </div>

        {/* WhatsApp Inbox — see conversations, reply, create cases */}
        <WhatsAppInboxAdmin />

        {/* Doctor Payouts dashboard (Feb 2026) */}
        <AdminEarnings />

        {/* Public Case Outcomes — admin curation */}
        <div className="mt-10" id="case-outcomes-admin">
          <CaseOutcomesAdmin />
        </div>
      </div>

      {/* Reject + auto-refund dialog */}
      <Dialog open={!!rejectFor} onOpenChange={(o) => { if (!o) { setRejectFor(null); setRejectReason(""); } }}>
        <DialogContent className="max-w-md" data-testid="admin-reject-case-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <X className="w-5 h-5"/> Reject case &amp; auto-refund
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Refunds the patient via Cashfree. Use for unresponsive doctors, fraud, or duplicate bookings.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-700 leading-relaxed">
              Patient <b>{rejectFor?.patient_name}</b> will be refunded{" "}
              <b className="text-emerald-700">₹{(rejectFor?.amount || 0).toLocaleString()}</b>{" "}
              instantly via Cashfree.
            </p>
            <div>
              <Label htmlFor="admin-reject-reason">Reason</Label>
              <Textarea id="admin-reject-reason" value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="e.g., Doctor unresponsive · case re-routed."
                        className="mt-1 min-h-[100px]"
                        data-testid="admin-reject-reason-input" />
              <div className="text-[10px] text-slate-500 mt-1">{rejectReason.length}/500 · min 10 characters</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectFor(null); setRejectReason(""); }}
                    data-testid="admin-reject-cancel-btn">Cancel</Button>
            <Button onClick={submitAdminReject} disabled={rejecting || rejectReason.trim().length < 10}
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                    data-testid="admin-reject-confirm-btn">
              {rejecting ? "Refunding…" : "Reject & refund"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// OnboardingNudgeButton — admin trigger for the doctor profile-completeness
// nudge. Runs a dry-run first to preview how many doctors would be nudged,
// then confirms before actually firing WhatsApp + email.
// ---------------------------------------------------------------------------
function OnboardingNudgeButton() {
  const [busy, setBusy] = React.useState(false);
  const [preview, setPreview] = React.useState(null);

  const dryRun = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/doctors/onboarding-nudge?dry_run=true");
      setPreview(r.data);
      if (!r.data.count) toast.success("No doctors need nudging right now — all profiles are complete!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setBusy(false); }
  };

  const fire = async () => {
    if (!preview) return;
    if (!window.confirm(`Send onboarding WhatsApp + email to ${preview.count} doctor(s)? This is idempotent — each doctor gets it at most once.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/doctors/onboarding-nudge");
      toast.success(`Nudge sent to ${r.data.fired.length}. Failed: ${r.data.failed.length}.`);
      setPreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nudge failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 rounded-xl border border-violet-200 bg-violet-50/50 p-4 flex items-start gap-3 flex-wrap" data-testid="admin-onboarding-nudge">
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-wider text-violet-700">Growth · one-tap</div>
        <div className="mt-0.5 font-heading font-semibold text-slate-900">Doctor onboarding nudge</div>
        <p className="text-xs text-slate-600 mt-1">
          Sends a WhatsApp + email to every approved doctor whose profile is missing photo, bio, fee or intro video.
          Each doctor gets it at most once (marked as nudged after the first successful send).
        </p>
        {preview && (
          <div className="mt-3 text-xs text-slate-700 bg-white border border-violet-200 rounded p-2.5">
            <b>{preview.count}</b> doctor(s) would be nudged.
            {preview.candidates?.length > 0 && (
              <ul className="mt-1.5 space-y-0.5 max-h-40 overflow-y-auto">
                {preview.candidates.slice(0, 8).map((c, i) => (
                  <li key={i} className="text-[11px]">
                    <span className="font-semibold">{c.name}</span>
                    <span className="text-slate-400"> — missing: </span>
                    <span className="text-violet-700">{c.gaps.join(", ")}</span>
                  </li>
                ))}
                {preview.candidates.length > 8 && <li className="text-[11px] text-slate-500">…and {preview.candidates.length - 8} more</li>}
              </ul>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2 shrink-0">
        {!preview ? (
          <Button size="sm" onClick={dryRun} disabled={busy} data-testid="admin-nudge-preview-btn"
                  className="bg-violet-600 hover:bg-violet-700 text-white">
            {busy ? "Loading…" : "Preview candidates"}
          </Button>
        ) : (
          <>
            <Button size="sm" onClick={fire} disabled={busy || !preview.count}
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                    data-testid="admin-nudge-fire-btn">
              {busy ? "Sending…" : `Send to ${preview.count}`}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setPreview(null)} disabled={busy}>
              Cancel
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
